import { Link } from "react-router-dom";
import {
  ArrowRight, Compass, Shield, Handshake, Settings, Wallet,
  Users, CheckCircle, Globe, Lightbulb, DollarSign, Target
} from "lucide-react";

const cioServices = [
  {
    icon: Compass,
    title: "IT Strategy & Digital Transformation",
    items: [
      "Tailored IT roadmaps aligned to business goals",
      "Digital capability models for maturity assessment",
      "Innovation through AI, automation, and emerging tech",
      "Alignment of IT investments to business growth",
    ],
  },
  {
    icon: Shield,
    title: "IT Governance & Risk Management",
    items: [
      "Enterprise governance frameworks for accountability",
      "Compliance with SEBI, RBI, Statuary regulations",
      "Strengthened cybersecurity and risk resilience",
      "Transparent reporting and audit readiness",
    ],
  },
  {
    icon: Handshake,
    title: "Business-IT Alignment",
    items: [
      "Clear communication between business and IT stakeholders",
      "Translation of technical initiatives to measurable outcomes",
      "Improved collaboration across leadership teams",
    ],
  },
  {
    icon: Settings,
    title: "IT Portfolio & Operations Management",
    items: [
      "Optimization of IT assets, infrastructure, service delivery",
      "KPI-driven monitoring for performance visibility",
      "Balanced focus on efficiency and innovation",
    ],
  },
  {
    icon: Wallet,
    title: "Cost Optimization & Sourcing",
    items: [
      "IT spend analysis, cloud/SaaS optimization",
      "Outsourcing vs. in-house strategy assessment",
      "Sustainable cost savings without compromising growth",
    ],
  },
];

const benefits = [
  { icon: Users, title: "Scalable Leadership", desc: "Access expertise when and where you need it" },
  { icon: CheckCircle, title: "Compliance Ready", desc: "Navigate complex regulatory environments" },
  { icon: Lightbulb, title: "Innovation Driven", desc: "Stay ahead with emerging technologies" },
  { icon: DollarSign, title: "Cost Efficient", desc: "Optimize budgets without sacrificing performance" },
  { icon: Target, title: "Business Focused", desc: "Position IT as a strategic enabler" },
];

const engagementModels = [
  { title: "Fractional CIO", desc: "Part-time executive leadership tailored for SMEs that need strategic IT direction without full-time overhead." },
  { title: "Interim CIO", desc: "Temporary leadership during transitions, crises, or organizational restructuring to maintain IT continuity." },
  { title: "Virtual CIO (vCIO)", desc: "Remote advisory, governance support, and strategic planning for organizations with distributed teams." },
  { title: "Strategic CIO Partner", desc: "Long-term collaboration for enterprise transformation, working alongside your leadership team." },
];

export default function CIOServicePage() {
  return (
    <div data-testid="cio-service-page">
      {/* Hero */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-4">
                CIO as a Service
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none text-[#0A0A0A] mb-8">
                Strategic IT Leadership, On Demand
              </h1>
              <p className="text-base lg:text-lg text-[#4B5563] leading-relaxed mb-8">
                We provide executive-level IT leadership without the cost and complexity
                of a full-time CIO. Our experts deliver strategic direction, governance,
                and digital transformation guidance — ensuring technology aligns with your
                business objectives.
              </p>
              <Link
                to="/contact"
                data-testid="cio-hero-cta"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#002FA7] text-white text-sm font-medium hover:bg-[#001F70] transition-colors"
              >
                Schedule a Consultation <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="overflow-hidden lg:overflow-visible">
              <img
                src="/cio-service.png"
                alt="CIO Strategic IT Leadership"
                className="w-full h-auto lg:h-[400px] lg:object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section data-testid="cio-services-section" className="py-20 lg:py-28 bg-[#F3F4F6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-14">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">
              What We Deliver
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A]">
              Comprehensive CIO Services
            </h2>
          </div>
          <div className="space-y-0 border border-gray-200 bg-white">
            {cioServices.map((service, i) => (
              <div
                key={service.title}
                data-testid={`cio-service-item-${i}`}
                className={`grid grid-cols-1 lg:grid-cols-3 ${
                  i < cioServices.length - 1 ? "border-b border-gray-200" : ""
                }`}
              >
                <div className="p-8 lg:p-10 lg:border-r border-gray-200 flex items-start gap-4">
                  <service.icon className="w-6 h-6 text-[#002FA7] shrink-0 mt-0.5" />
                  <h3 className="font-heading text-lg font-semibold text-[#0A0A0A]">
                    {service.title}
                  </h3>
                </div>
                <div className="lg:col-span-2 p-8 lg:p-10">
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {service.items.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-[#4B5563]">
                        <span className="w-1 h-1 rounded-full bg-[#002FA7] mt-2 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section data-testid="cio-benefits-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-14">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">
              Why Choose CIO as a Service
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A]">
              Business-driven technology leadership
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-0 border border-gray-200">
            {benefits.map((b, i) => (
              <div
                key={b.title}
                className={`p-6 lg:p-8 ${i < 4 ? "lg:border-r border-gray-200" : ""} ${
                  i < 3 ? "sm:border-b lg:border-b-0 border-gray-200" : ""
                }`}
              >
                <b.icon className="w-5 h-5 text-[#002FA7] mb-4" />
                <h4 className="text-sm font-semibold text-[#0A0A0A] mb-2">{b.title}</h4>
                <p className="text-xs text-[#4B5563] leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement Models */}
      <section data-testid="engagement-models-section" className="py-20 lg:py-28 bg-[#F3F4F6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-14">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">
              Engagement Models
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A]">
              Flexible partnership options
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border border-gray-200 bg-white">
            {engagementModels.map((model, i) => (
              <div
                key={model.title}
                data-testid={`engagement-model-${i}`}
                className={`p-8 lg:p-12 ${i % 2 === 0 ? "sm:border-r border-gray-200" : ""} ${
                  i < 2 ? "border-b border-gray-200" : ""
                }`}
              >
                <h3 className="font-heading text-xl font-semibold text-[#0A0A0A] mb-3">
                  {model.title}
                </h3>
                <p className="text-sm text-[#4B5563] leading-relaxed">{model.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#002FA7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-white mb-6">
            Transform IT from cost center to strategic asset
          </h2>
          <p className="text-base text-blue-200 max-w-xl mx-auto mb-10 leading-relaxed">
            Get executive insight and hands-on execution that brings clarity,
            resilience, and competitive advantage to your organization.
          </p>
          <Link
            to="/contact"
            data-testid="cio-cta-link"
            className="inline-flex items-center gap-2 px-10 py-4 bg-white text-[#002FA7] text-sm font-semibold hover:bg-gray-100 transition-colors"
          >
            Get Started <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
